import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useSearch } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { getKennelStatusBadgeClass, formatKennelStatus } from "@/lib/badge-styles";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dog,
  Cat,
  MapPin,
  Calendar,
  Weight,
  Heart,
  AlertTriangle,
  FileText,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Activity,
  Stethoscope,
  Home,
  Share,
  TrendingUp,
  TrendingDown,
  Info,
  HelpCircle,
  Sparkles,
  Loader2,
  RefreshCw,
  MoreVertical,
  Flame,
  Clock,
  Award,
  MessageSquare,
  Flag,
  Tag,
  PawPrint,
  Palette,
  User,
  Building2,
  Cpu,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { useBreadcrumbs } from "@/contexts/breadcrumb-context";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format, isToday, isYesterday, differenceInDays } from "date-fns";
import { Link } from "wouter";
import { FollowAnimalButton } from "@/components/follow-animal-button";
import { EvalStatusStrip } from "@/components/animals/StatusHistoryCard";

// Format date in UTC to avoid timezone shift issues
function formatDateUTC(dateStr: string, formatStr: string = "MMM d, yyyy h:mm a"): string {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const date = new Date(isDateOnly ? dateStr + 'T00:00:00Z' : (dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'));
  if (isNaN(date.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  if (formatStr === "MMM d, yyyy") {
    return `${month} ${day}, ${year}`;
  }
  const hours = date.getUTCHours();
  const mins = date.getUTCMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${month} ${day}, ${year} ${hour12}:${mins} ${ampm}`;
}
function formatRelativeDate(dateStr: string): string {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const date = isDateOnly ? new Date(dateStr + "T00:00:00") : new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const localMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (isToday(localMidnight)) return "Today";
  if (isYesterday(localMidnight)) return "Yesterday";
  return format(localMidnight, "EEE, MMM d, yyyy");
}

import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/page-header";
import { useAuth } from "@/lib/auth-context";
import { LabelWithHover } from "@/components/ui/LabelWithHover";
import { formatReportDate, copyToClipboard } from "@/lib/utils";
import { getScoreBandColor } from "@/lib/score-utils";
import { api, apiRequest, queryClient } from "@/lib/queryClient";
import { normalizeSummaryForRender } from "@/lib/normalize-summary";
import { AnimalSummaryDrawer } from "@/components/animals/AnimalSummaryDrawer";
import InteractionDetailDrawer from "@/components/interactions/InteractionDetailDrawer";
import { EntityLinkerDialog } from "@/components/tasks/entity-linker-dialog";
import { AnimalActivityTimeline } from "@/components/animals/AnimalActivityTimeline";
import { HistoryGapMarker } from "@/components/animals/HistoryGapMarker";
import { AnimalInteractionTimeline, type Interaction } from "@/components/animals/AnimalInteractionTimeline";
import { LinkedReportsList } from "@/components/animals/linked-reports-list";
import { History, Search, Link as LinkIcon, X, ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { 
  FieldChange, 
  getTimelineChangeLabel, 
  deduplicateStatusChanges 
} from "@/lib/timeline-utils";
import { useAiEnabled } from "@/hooks/use-ai-enabled";
import { AnimalTimelineCard } from "@/components/animals/AnimalTimelineCard";
import { AnimalFilesTab } from "@/components/animals/AnimalFilesTab";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { AnimalProfileTab, AnimalProfileData } from "@/components/animals/AnimalProfileTab";
import { PublicAdoptionListingStatusBadge } from "@/components/animals/PublicAdoptionListingStatusBadge";
import { AnimalChecklistProgress } from "@/components/animals/AnimalChecklistProgress";
import { AdoptionListingChecklistItem, ChecklistItemDisplay } from "@/components/animals/AdoptionListingChecklistItem";
import { EvaluationChecklistPendingItem } from "@/components/animals/EvaluationChecklistPendingItem";
import { HighlightInteractionButton } from "@/components/interactions/HighlightInteractionButton";
import { FlagReason } from "@/lib/interaction-types";
import { HIGHLIGHTED_FILTER_PARAM } from "@/lib/constants";
import { StatusDaysBadge } from "@/components/animals/StatusDaysBadge";
import { Copy } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useZoneLabels } from "@/hooks/use-zone-labels";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useReportFreshness } from "@/hooks/use-report-freshness";
import { ReportFreshnessModal } from "@/components/report-freshness-modal";
import { getStayColor } from "@/lib/stay-colors";

interface TimelineSnapshot {
  reportDate: string;
  location: string;
  zoneCode: string | null;
  kennelStatusNorm: string | null;
  substatusNorm: string | null;
  statusCode: string | null;
  adoptionLevelNorm: string | null;
  intakeDate: string | null;
  animalEvalStatus: string | null; // Gate E4.2: Animal Evaluation status
  animalEvalRaw: string | null; // Gate E4.2: Animal Evaluation raw value
  changesFromPrevious: FieldChange[];
}

interface Stay {
  stayIndex: number;
  startDate: string;
  endDate: string;
  outcomedInReports: boolean; // Gate E3.3: true if stay ended before latest report date
  outcomeDate?: string | null; // K10: from animal_stays
  outcomeReason?: string | null; // K10: from animal_stays
  linkedToPriorStay?: boolean; // P5: true for transfer receiving legs
  snapshots: TimelineSnapshot[];
}

interface Timeline {
  stays: Stay[];
}

interface Animal {
  id: string;
  animalId: string;
  name: string;
  species: "DOG" | "CAT" | "OTHER";
  primaryBreed?: string;
  color?: string;
  sex?: string;
  microchipId?: string;
  photoUrl: string | null;
  currentStatus: {
    location: string;
    zoneCode: string;
    kennelStatusNorm?: "AVAILABLE" | "UNAVAIL" | "STRAY_WAIT" | "EVALUATION";
    substatusNorm?: "MEDICAL" | "BEHAVIOR" | "BEH+MED" | "PENDADOPT";
    adoptionLevelNorm?: string;
    lastSeenDate: string;
    kennelStatusSince?: string | null;
    kennelStatusSinceIsApprox?: boolean;
  };
  demographics: {
    ageDisplay?: string;
    weightLb?: number;
    intakeDate?: string;
    originOrgNorm?: string;
    originHistory?: string[];
  };
  medical: {
    medicalDx?: string[];
    hasMedicalNotes: boolean;
  };
  evaluation: {
    status: string | null;
    label: string | null;
    raw: string | null;
    lastEvaluatedAt: string | null;
  };
  timeline?: Timeline;
  isOutcomed: boolean; // Gate E3.4
  outcomeDate?: string | null; // K10
  outcomeReason?: string | null; // K10
  intakeDate?: string | null; // K10
  lastObservationDate?: string | null;
  dataStartDate?: string | null;
}

interface InteractionsResponse {
  interactions: Interaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface SeverityDistribution {
  greenCount: number;
  yellowCount: number;
  redCount: number;
  unknownCount: number;
}

interface ScoreCodeCounts {
  noScore: number;
  "1": number;
  "2": number;
  "3A": number;
  "3B": number;
  "4A": number;
  "4B": number;
  "5A": number;
  "5B": number;
}

interface BehaviorRollups {
  totalInteractions: number;
  firstInteractionAt: string | null;
  mostRecentInteractionAt: string | null;
  highestScoreEver: string | null;
  highestSeverityBandEver: string | null;
  highSeverityCountAllTime: number;
  severityDistributionAllTime: SeverityDistribution;
  scoreCodeCountsAllTime: ScoreCodeCounts;
  scoreCodeCountsLast7: ScoreCodeCounts;
  scoreCodeCountsLast14: ScoreCodeCounts;
  scoreCodeCountsLast30: ScoreCodeCounts;
  totalInteractionsLast7: number;
  highSeverityCountLast7: number;
  severityDistributionLast7: SeverityDistribution;
  totalInteractionsLast14: number;
  highSeverityCountLast14: number;
  severityDistributionLast14: SeverityDistribution;
  totalInteractionsLast30: number;
  highSeverityCountLast30: number;
  severityDistributionLast30: SeverityDistribution;
}

interface TrendMetaWindowed {
  timezone: string;
  windowDays: number;
  severityDefinition: string;
  thresholdX: number;
  minSampleSize: number;
  delta: number | null;
  sampleSizes: { last: number; prior: number };
  hiRate: { last: number | null; prior: number | null };
  comparedWindows: {
    last: { start: string; end: string };
    prior: { start: string; end: string };
  };
}

interface BehaviorTrendWindowed {
  label: "Improving" | "Stable" | "Worsening" | "Insufficient data";
  meta: TrendMetaWindowed;
}

interface SummaryContent {
  bullets: string[];
  trendNarrative: string;
}

interface SummaryAudit {
  model: string;
  generatedAt: string;
  promptVersion: string;
  sourceInteractionIds: string[];
  tokenUsage: { prompt: number; completion: number };
  promptConfigHash: string;
}

interface BehaviorSummary {
  status: "disabled" | "unavailable" | "never_generated" | "generating" | "fresh" | "stale" | "error";
  content: SummaryContent | null;
  audit: SummaryAudit | null;
  errorMessage: string | null;
  lastGeneratedAt: string | null;
}

interface BehaviorAlerts {
  computedAt: string;
  strugglingNow: {
    flagged: boolean;
    count: number;
    windowHours: number;
    threshold: number;
  };
  trend3d: {
    label: "Improving" | "Worsening" | "Stable" | "Insufficient data" | null;
    delta: number | null;
    lastCount: number;
    priorCount: number;
    lastHiRate: number | null;
    priorHiRate: number | null;
    threshold: number;
    minSample: number;
  };
  recentHighSeverity: {
    count: number;
    flagged: boolean;
    windowDays: number;
    threshold: number;
  };
  streak: {
    days: number;
    flagged: boolean;
    startDate: string | null;
    endDate: string | null;
    threshold: number;
  };
  dailySpike: {
    flagged: boolean;
    date: string | null;
    rate: number | null;
    baselineRate: number | null;
    multiplier: number | null;
    thresholdMultiplier: number;
  };
  // Gate R4: flag signals
  hasLongNoteFlag: boolean;
  hasKeywordFlag: boolean;
  hasHighlightFlag: boolean;
}

interface BehaviorContext {
  animalId: string;
  rollups: BehaviorRollups;
  trend7: BehaviorTrendWindowed;
  trend30: BehaviorTrendWindowed;
  summary: BehaviorSummary | null;
  alerts: BehaviorAlerts | null;
}

function InteractionScoreDisplay({ scoreCode, scoreBand }: { scoreCode: string | null; scoreBand: string | null }) {
  if (!scoreCode) {
    return <span className="text-muted-foreground">–</span>;
  }

  return (
    <span className="flex items-center gap-1">
      <span className="font-medium">{scoreCode}</span>
      {scoreBand && (
        <Badge 
          variant="outline" 
          className={`text-xs px-1.5 py-0 h-5 ${getScoreBandColor(scoreBand)}`}
        >
          {scoreBand}
        </Badge>
      )}
    </span>
  );
}

function AnimalHighlightedInteractionCount({ animalId }: { animalId: string }) {
  const [, navigate] = useLocation();
  const { data } = useQuery<{ count: number }>({
    queryKey: ['/api/animals', animalId, 'interactions', 'highlighted-count'],
    queryFn: async () => {
      const res = await fetch(`/api/animals/${encodeURIComponent(animalId)}/interactions/highlighted-count`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch highlighted count');
      return res.json();
    },
    enabled: !!animalId,
  });
  if (!data || data.count === 0) return null;
  const count = data.count;
  const href = `/interactions?${HIGHLIGHTED_FILTER_PARAM}=true&animalId=${encodeURIComponent(animalId)}`;
  return (
    <div
      role="button"
      tabIndex={0}
      className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 mt-1 cursor-pointer hover:text-amber-900 dark:hover:text-amber-300 transition-colors"
      onClick={() => navigate(href)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(href); }}
      data-testid="link-highlighted-interaction-count"
    >
      <Flag className="h-3 w-3 fill-current" />
      <span>{count} highlighted interaction{count !== 1 ? 's' : ''}</span>
    </div>
  );
}

function CollapsibleNote({ note, maxLength = 100 }: { note: string; maxLength?: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const shouldTruncate = note.length > maxLength;
  const truncated = shouldTruncate ? note.substring(0, maxLength) + "…" : note;

  if (!shouldTruncate) {
    return <span className="text-sm text-muted-foreground">{note}</span>;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button 
          className="flex items-center gap-1 text-left text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          data-testid="button-expand-interaction-note"
        >
          <span>{isOpen ? note : truncated}</span>
          {isOpen ? (
            <ChevronUp className="h-3 w-3 shrink-0" />
          ) : (
            <ChevronDown className="h-3 w-3 shrink-0" />
          )}
        </button>
      </CollapsibleTrigger>
    </Collapsible>
  );
}

function AlertBadgesStrip({ alerts, animalId }: { alerts: BehaviorAlerts | null | undefined; animalId: string }) {
  const [, setLocation] = useLocation();

  if (!alerts) {
    return null;
  }

  const hasAnyBehavioralAlert = alerts.strugglingNow.flagged || alerts.streak.flagged || alerts.dailySpike.flagged || alerts.recentHighSeverity.flagged || alerts.trend3d.label === "Worsening";
  const hasAnyFlagSignal = alerts.hasLongNoteFlag || alerts.hasKeywordFlag || alerts.hasHighlightFlag;

  if (!hasAnyBehavioralAlert && !hasAnyFlagSignal) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="alert-badges-strip">
      {alerts.strugglingNow.flagged && (
        <Badge 
          variant="outline" 
          className="bg-alert/10 text-alert border-alert/30 text-xs px-2 py-0.5"
          data-testid="badge-struggling-now"
        >
          <AlertTriangle className="h-3 w-3 mr-1" />
          Struggling ({alerts.strugglingNow.count} in {alerts.strugglingNow.windowHours}h)
        </Badge>
      )}
      {alerts.streak.flagged && (
        <Badge 
          variant="outline" 
          className="bg-warning/10 text-warning border-warning/30 text-xs px-2 py-0.5"
          data-testid="badge-streak"
        >
          <Flame className="h-3 w-3 mr-1" />
          {alerts.streak.days}-day streak
        </Badge>
      )}
      {alerts.dailySpike.flagged && (
        <Badge 
          variant="outline" 
          className="bg-warning/10 text-warning border-warning/30 text-xs px-2 py-0.5"
          data-testid="badge-spike"
        >
          <TrendingUp className="h-3 w-3 mr-1" />
          Spike {alerts.dailySpike.multiplier ? `(${alerts.dailySpike.multiplier.toFixed(1)}x)` : ""}
        </Badge>
      )}
      {alerts.recentHighSeverity.flagged && !alerts.strugglingNow.flagged && (
        <Badge 
          variant="outline" 
          className="bg-muted text-muted-foreground text-xs px-2 py-0.5"
          data-testid="badge-recent-high"
        >
          {alerts.recentHighSeverity.count} high-sev ({alerts.recentHighSeverity.windowDays}d)
        </Badge>
      )}
      {alerts.trend3d.label === "Worsening" && (
        <Badge 
          variant="outline" 
          className="bg-warning/10 text-warning border-warning/30 text-xs px-2 py-0.5"
          data-testid="badge-trend-worsening"
        >
          <TrendingDown className="h-3 w-3 mr-1" />
          3d worsening
        </Badge>
      )}
      {alerts.hasLongNoteFlag && (
        <Badge
          variant="outline"
          className="bg-info/10 text-info border-info/30 text-xs px-2 py-0.5 cursor-pointer"
          data-testid="badge-long-note-flag"
          onClick={() => setLocation(`/interactions?animalId=${encodeURIComponent(animalId)}&flagTypes=long_note&startDate=2000-01-01`)}
        >
          <FileText className="h-3 w-3 mr-1" />
          Long note flag
        </Badge>
      )}
      {alerts.hasKeywordFlag && (
        <Badge
          variant="outline"
          className="bg-info/10 text-info border-info/30 text-xs px-2 py-0.5 cursor-pointer"
          data-testid="badge-keyword-flag"
          onClick={() => setLocation(`/interactions?animalId=${encodeURIComponent(animalId)}&flagTypes=keyword&startDate=2000-01-01`)}
        >
          <Tag className="h-3 w-3 mr-1" />
          Keyword flag
        </Badge>
      )}
      {alerts.hasHighlightFlag && (
        <Badge
          variant="outline"
          className="bg-highlight/10 text-highlight border-highlight/30 text-xs px-2 py-0.5 cursor-pointer"
          data-testid="badge-highlight-flag"
          onClick={() => setLocation(`/interactions?${HIGHLIGHTED_FILTER_PARAM}=true&animalId=${encodeURIComponent(animalId)}&startDate=2000-01-01`)}
        >
          <Flag className="h-3 w-3 mr-1" />
          Highlighted
        </Badge>
      )}
    </div>
  );
}

type ObsHistoryRange = "all" | "30" | "14" | "7";

const SCORE_CODES = ["1", "2", "3A", "3B", "4A", "4B", "5A", "5B"] as const;

function getScoreCodeBand(code: string): "GREEN" | "YELLOW" | "RED" {
  if (code === "1" || code === "2") return "GREEN";
  if (code === "3A" || code === "3B") return "YELLOW";
  return "RED";
}

function getScoreCodeBandClasses(code: string): string {
  const band = getScoreCodeBand(code);
  switch (band) {
    case "GREEN":
      return "border-success/30 bg-success/10";
    case "YELLOW":
      return "border-warning/30 bg-warning/10";
    case "RED":
      return "border-destructive/30 bg-destructive/10";
  }
}

function BehaviorRollupsSection({ 
  rollups, 
  alerts,
  animalId,
  isLoading, 
  isError,
  activeScoreFilter,
  activeUnscoredOnly,
  onScoreSelect,
  onUnscoredSelect,
}: { 
  rollups: BehaviorRollups | undefined | null; 
  alerts: BehaviorAlerts | undefined | null;
  animalId: string;
  isLoading: boolean; 
  isError: boolean;
  activeScoreFilter: string | null;
  activeUnscoredOnly: boolean;
  onScoreSelect: (code: string | null) => void;
  onUnscoredSelect: () => void;
}) {
  const [range, setRange] = useState<ObsHistoryRange>("all");

  if (isLoading) {
    return (
      <div className="space-y-3 p-3 bg-muted/30 rounded-md" data-testid="rollups-loading">
        <Skeleton className="h-4 w-24" />
        <div className="grid grid-cols-4 gap-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !rollups) {
    return (
      <div className="p-3 bg-muted/30 rounded-md" data-testid="rollups-error">
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" />
          Snapshot unavailable
        </p>
      </div>
    );
  }

  function getCountsForRange(r: ObsHistoryRange): ScoreCodeCounts {
    switch (r) {
      case "7": return rollups!.scoreCodeCountsLast7;
      case "14": return rollups!.scoreCodeCountsLast14;
      case "30": return rollups!.scoreCodeCountsLast30;
      default: return rollups!.scoreCodeCountsAllTime;
    }
  }

  function getTotalForRange(r: ObsHistoryRange): number {
    switch (r) {
      case "7": return rollups!.totalInteractionsLast7;
      case "14": return rollups!.totalInteractionsLast14;
      case "30": return rollups!.totalInteractionsLast30;
      default: return rollups!.totalInteractions;
    }
  }

  function getHighSevForRange(r: ObsHistoryRange): number {
    switch (r) {
      case "7": return rollups!.highSeverityCountLast7;
      case "14": return rollups!.highSeverityCountLast14;
      case "30": return rollups!.highSeverityCountLast30;
      default: return rollups!.highSeverityCountAllTime;
    }
  }

  const counts = getCountsForRange(range);
  const totalForRange = getTotalForRange(range);
  const highSevForRange = getHighSevForRange(range);

  const daysSinceLast = rollups.mostRecentInteractionAt
    ? Math.floor((Date.now() - new Date(rollups.mostRecentInteractionAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="space-y-4" data-testid="behavior-rollups-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-medium" data-testid="text-observation-history-title">Observation History</p>
        <Select value={range} onValueChange={(v) => setRange(v as ObsHistoryRange)}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="select-obs-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="option-range-all">All Time</SelectItem>
            <SelectItem value="30" data-testid="option-range-30">Last 30 Days</SelectItem>
            <SelectItem value="14" data-testid="option-range-14">Last 14 Days</SelectItem>
            <SelectItem value="7" data-testid="option-range-7">Last 7 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border p-3 space-y-2" data-testid="observation-date-range">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          <div className="flex items-center gap-2" data-testid="text-first-observation">
            <span className="text-xs text-muted-foreground">First Observation</span>
            <span className="text-sm font-medium">
              {rollups.firstInteractionAt
                ? format(new Date(rollups.firstInteractionAt), "MMM d, yyyy")
                : "None"}
            </span>
          </div>
          <div className="flex items-center gap-2" data-testid="text-last-observation">
            <span className="text-xs text-muted-foreground">Last Observation</span>
            <span className="text-sm font-medium">
              {rollups.mostRecentInteractionAt
                ? <>
                    {format(new Date(rollups.mostRecentInteractionAt), "MMM d, yyyy")}
                    {daysSinceLast !== null && daysSinceLast > 0 && (
                      <span className="text-muted-foreground font-normal ml-1">({daysSinceLast}d ago)</span>
                    )}
                  </>
                : "None"}
            </span>
            {(() => {
              const lastDate = rollups.mostRecentInteractionAt;
              const days = lastDate ? Math.floor((Date.now() - new Date(lastDate).getTime()) / (1000 * 60 * 60 * 24)) : null;
              const isStale = !lastDate || (days !== null && days >= 7);
              if (!isStale) return null;
              const colorClass = !lastDate
                ? "text-red-600 dark:text-red-500"
                : "text-amber-600 dark:text-amber-500";
              return (
                <MessageSquare
                  className={`h-4 w-4 flex-shrink-0 ${colorClass}`}
                  data-testid="icon-observation-nudge"
                />
              );
            })()}
          </div>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="behavior-range-summary">
          <span className="font-semibold text-foreground tabular-nums">{totalForRange}</span> observation{totalForRange !== 1 ? "s" : ""} total
          {highSevForRange > 0 && (
            <span className="before:content-['·'] before:mx-1.5"><span className="font-semibold text-foreground tabular-nums">{highSevForRange}</span> high severity</span>
          )}
        </p>
      </div>

      <AlertBadgesStrip alerts={alerts} animalId={animalId} />

      <div className="grid grid-cols-4 sm:grid-cols-9 gap-2" data-testid="score-code-counts-grid">
        <div
          className={`relative rounded-md border p-2 text-center transition-opacity cursor-pointer toggle-elevate ${
            counts.noScore > 0 ? "bg-muted/50 border-border" : "bg-transparent opacity-60 border-border"
          } ${activeUnscoredOnly ? "toggle-elevated" : ""}`}
          data-testid="score-count-no-score"
          onClick={() => onUnscoredSelect()}
        >
          <p className="text-sm font-semibold tabular-nums">{counts.noScore}</p>
          <p className="text-[10px] text-muted-foreground">N/S</p>
        </div>
        {SCORE_CODES.map((code) => {
          const count = counts[code];
          const hasObs = count > 0;
          return (
            <div
              key={code}
              className={`relative rounded-md border p-2 text-center transition-opacity cursor-pointer toggle-elevate ${
                hasObs ? getScoreCodeBandClasses(code) : "bg-transparent opacity-60 border-border"
              } ${activeScoreFilter === code ? "toggle-elevated" : ""}`}
              data-testid={`score-count-${code}`}
              onClick={() => onScoreSelect(code === activeScoreFilter ? null : code)}
            >
              <p className="text-sm font-semibold tabular-nums">{count}</p>
              <p className="text-[10px] text-muted-foreground">{code}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface AISummarySectionProps {
  summary: BehaviorSummary | undefined | null;
  animalId: string;
  isLoading: boolean;
  onGenerate: () => void;
  isGenerating: boolean;
  canGenerate: boolean;
}

function AISummarySection({ 
  summary, 
  animalId, 
  isLoading,
  onGenerate,
  isGenerating,
  canGenerate
}: AISummarySectionProps) {
  if (isLoading) {
    return (
      <div className="space-y-3 p-3 bg-muted/30 rounded-md" data-testid="summary-behavior-container">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  // Gate I13: New Summary button in header, replaces timestamp
  const renderNewSummaryButton = () => {
    if (!canGenerate) return null;
    
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={onGenerate}
        disabled={isGenerating}
        className="gap-1.5"
        data-testid="button-generate-summary"
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="h-3.5 w-3.5" />
            New Summary
          </>
        )}
      </Button>
    );
  };

  const renderSummaryHeader = () => (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        <Sparkles className="h-3 w-3" />
        <Label className="text-xs font-medium uppercase tracking-wider">Interaction Insights</Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <button 
              className="p-0.5 rounded-sm hover:bg-muted transition-colors cursor-help"
              data-testid="summary-ai-tooltip-trigger"
              aria-label="About AI summaries"
            >
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs text-xs space-y-1">
            <p>This summary is AI-generated from accepted interaction notes.</p>
            <p>You can refine terminology and tone in Admin Settings → AI (System Instructions, Style Guidance, and Model).</p>
            <p className="text-muted-foreground">The summary is non-authoritative and may be incomplete.</p>
          </TooltipContent>
        </Tooltip>
      </div>
      {/* Gate I13: New Summary button replaces timestamp */}
      {renderNewSummaryButton()}
    </div>
  );

  return (
    <div className="space-y-3 p-3 bg-muted/30 rounded-md" data-testid="summary-behavior-container">
      {renderSummaryHeader()}
      
      {summary.status === "disabled" && (
        <p className="text-sm text-muted-foreground" data-testid="summary-status-text">
          AI summaries are disabled.
        </p>
      )}

      {summary.status === "unavailable" && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground" data-testid="summary-status-text">
            Summary unavailable.
          </p>
          {summary.errorMessage?.includes("API key") ? (
            <p className="text-xs text-muted-foreground">
              Ask an admin to configure OpenAI settings.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              A summary can be generated after at least one accepted interaction exists.
            </p>
          )}
        </div>
      )}

      {summary.status === "never_generated" && (
        <p className="text-sm text-muted-foreground" data-testid="summary-status-text">
          No summaries have been generated yet.
        </p>
      )}

      {summary.status === "generating" && (
        <div 
          className="flex items-center gap-2 text-sm text-muted-foreground" 
          data-testid="summary-generating"
          aria-live="polite"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Generating summary...
        </div>
      )}

      {/* Gate I13: fresh/stale no longer display content here - history section shows all summaries */}

      {summary.status === "error" && (
        <div className="space-y-2">
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            Summary error
          </Badge>
          {summary.errorMessage && (
            <p 
              className="text-xs text-destructive line-clamp-3"
              data-testid="summary-error-message"
            >
              {summary.errorMessage}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Gate I13: Summary History Section
interface SummaryHistoryItem {
  id: number;
  timeframeType: string;
  customQuestions: string | null;
  preview: string | null;
  generatedAt: string;
}

interface SummaryHistorySectionProps {
  history: SummaryHistoryItem[] | undefined;
  isLoading: boolean;
  animalId: string;
}

const TIMEFRAME_LABELS: Record<string, string> = {
  all_time: "All time",
  last_7_days: "Last 7 days",
  last_14_days: "Last 14 days",
  last_30_days: "Last 30 days",
};

// Content can be a normalized object or a string
interface NormalizedContent {
  bullets: string[];
  trendNarrative?: string | null;
}

function SummaryHistorySection({ history, isLoading, animalId }: SummaryHistorySectionProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [fullContent, setFullContent] = useState<NormalizedContent | string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  
  const handleToggle = async (item: SummaryHistoryItem) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      setFullContent(null);
      return;
    }
    
    setExpandedId(item.id);
    setLoadingContent(true);
    
    try {
      const response = await fetch(
        `/api/summaries/history/${item.id}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to fetch full content');
      const data = await response.json();
      // Content could be a normalized object {bullets, trendNarrative} or a string
      setFullContent(data.content);
    } catch (error) {
      setFullContent("Failed to load content");
    } finally {
      setLoadingContent(false);
    }
  };
  
  // Render content properly whether it's an object or string
  const renderContent = () => {
    if (!fullContent) return null;
    
    // If it's a string (error or legacy), render directly
    if (typeof fullContent === 'string') {
      return <p className="text-sm">{fullContent}</p>;
    }
    
    // If it's a normalized object with bullets
    const normalized = fullContent as NormalizedContent;
    return (
      <div className="space-y-3">
        {normalized.bullets && normalized.bullets.length > 0 && (
          <ul className="list-disc list-inside space-y-1.5 text-sm text-foreground/90">
            {normalized.bullets.map((bullet, idx) => (
              <li key={idx} className="leading-relaxed">{bullet}</li>
            ))}
          </ul>
        )}
        {normalized.trendNarrative && (
          <div className="pt-2 border-t border-border/50">
            <p className="text-sm text-muted-foreground italic">
              {normalized.trendNarrative}
            </p>
          </div>
        )}
      </div>
    );
  };
  
  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="summary-history-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  
  if (!history || history.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-3" data-testid="summary-history-section">
      {history.map((item) => (
        <Collapsible 
          key={item.id} 
          open={expandedId === item.id}
          onOpenChange={() => handleToggle(item)}
        >
          <div className="border border-border rounded-md overflow-hidden">
            <CollapsibleTrigger asChild>
              <button
                className="w-full text-left p-3 hover:bg-muted/50 transition-colors"
                data-testid={`summary-history-item-${item.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{format(new Date(item.generatedAt), "MMM d, yyyy h:mm a")}</span>
                    <Badge variant="outline" className="text-xs">
                      {TIMEFRAME_LABELS[item.timeframeType] || item.timeframeType}
                    </Badge>
                    {item.customQuestions && (
                      <Badge variant="secondary" className="text-xs">
                        Custom Q
                      </Badge>
                    )}
                  </div>
                  {expandedId === item.id ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                {item.preview && expandedId !== item.id && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                    {item.preview}
                  </p>
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-3 py-3 bg-muted/30 border-t border-border">
                {loadingContent ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                ) : fullContent ? (
                  renderContent()
                ) : (
                  <p className="text-sm text-muted-foreground">No content available</p>
                )}
                {item.customQuestions && (
                  <div className="mt-2 pt-2 border-t border-border/50">
                    <p className="text-xs text-muted-foreground">
                      <strong>Custom questions:</strong> {item.customQuestions}
                    </p>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      ))}
    </div>
  );
}


function buildStatusSummary(snapshot: TimelineSnapshot): string {
  const parts: string[] = [];

  // Start with kennelStatusNorm
  if (snapshot.kennelStatusNorm) {
    const status = snapshot.kennelStatusNorm;
    if (status === "AVAILABLE") parts.push("Available");
    else if (status === "UNAVAIL") parts.push("Unavailable");
    else if (status === "STRAY_WAIT") parts.push("Stray wait");
    else if (status === "EVALUATION") parts.push("Under evaluation");
    else {
      // Title case the raw value
      parts.push(status.split('_').map(w => 
        w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
      ).join(' '));
    }
  } else if (snapshot.statusCode) {
    // Fallback to statusCode
    parts.push(`Status: ${snapshot.statusCode}`);
  }

  // Append substatus in parentheses
  if (snapshot.substatusNorm) {
    const substatus = snapshot.substatusNorm;
    if (substatus === "MEDICAL") parts.push("(medical)");
    else if (substatus === "BEHAVIOR") parts.push("(behavior)");
    else if (substatus === "BEH+MED") parts.push("(medical + behavior)");
    else if (substatus === "PENDADOPT") parts.push("(pending adoption)");
    else parts.push(`(${substatus.toLowerCase()})`);
  }

  // Append adoption level
  if (snapshot.adoptionLevelNorm) {
    parts.push(`· ${snapshot.adoptionLevelNorm}`);
  }

  // Return the combined string or fallback
  return parts.length > 0 ? parts.join(' ') : "Status not available";
}

export default function AnimalDetail() {
  const params = useParams();
  const animalId = params.id;
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const { user } = useAuth();
  const { isMobile } = useSidebar();

  type AnimalTab = "overview" | "profile" | "behavior" | "stays" | "medical" | "files";
  const VALID_TABS: AnimalTab[] = ["overview", "profile", "behavior", "stays", "medical", "files"];

  const [activeTab, setActiveTab] = useState<AnimalTab>(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && VALID_TABS.includes(tabParam as AnimalTab)) {
      return tabParam as AnimalTab;
    }
    return "overview";
  });

  const updateUrlParams = useCallback((updates: Record<string, string | null>) => {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === '') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }
    const newSearch = params.toString();
    const newPath = `/animals/${animalId}${newSearch ? `?${newSearch}` : ''}`;
    const currentPath = window.location.pathname + window.location.search;
    if (newPath === currentPath) {
      return;
    }
    navigate(newPath, { replace: true });
  }, [animalId, navigate]);

  useEffect(() => {
    updateUrlParams({
      tab: activeTab === "overview" ? null : activeTab,
    });
  }, [activeTab, updateUrlParams]);

  useEffect(() => {
    const currentTabParam = new URLSearchParams(searchString).get("tab");
    const parsed: AnimalTab = (currentTabParam && VALID_TABS.includes(currentTabParam as AnimalTab))
      ? (currentTabParam as AnimalTab)
      : "overview";
    if (parsed !== activeTab) {
      setActiveTab(parsed);
    }
  }, [searchString]);

  // S8.1-11: scroll to the Evaluation Checklist card when ?tab=behavior&section=checklist is present.
  // 150ms delay mirrors the drawer's Gate S8 context → setTimeout → scrollIntoView pattern.
  useEffect(() => {
    if (activeTab !== "behavior") return;
    if (new URLSearchParams(searchString).get("section") !== "checklist") return;
    const timer = setTimeout(() => {
      document.getElementById("evaluation-checklist")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(timer);
  }, [activeTab, searchString]);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as AnimalTab);
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTop = 0;
    }
  }, []);

  // AF.5: category state lives at page level so it survives tab switches
  // (e.g. user selects "Behavior" filter, switches to Stays tab, comes back — filter is preserved)
  const [activityCategory, setActivityCategory] = useState<string>("all");

  const queryClient = useQueryClient();
  const { isAiEnabled, isLoading: isAiLoading } = useAiEnabled();
  
  const canGenerateSummary = user?.role === "admin" || user?.role === "staff" || user?.role === "staff-coordinator" || user?.role === "ops";
  
  // Gate I13: Summary drawer state
  const [freshnessModalOpen, setFreshnessModalOpen] = useState(false);
  const { allPets: allPetsFreshness, interaction: interactionFreshness } = useReportFreshness();

  const [summaryDrawerOpen, setSummaryDrawerOpen] = useState(false);
  
  // Interaction detail drawer state
  const [interactionDrawerOpen, setInteractionDrawerOpen] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState<Interaction | null>(null);

  const [entityLinkerOpen, setEntityLinkerOpen] = useState(false);

  const { data: animal, isLoading } = useQuery<Animal>({
    queryKey: [`/api/animals/${animalId}`],
    enabled: !!animalId,
  });

  usePageTitle(animal ? `${animal.name} (${animal.animalId})` : undefined);
  const { resolveLabel } = useZoneLabels();

  const locationFreshness = useMemo(() => {
    if (!allPetsFreshness.locations.length || !animal?.currentStatus?.location) return null;
    return allPetsFreshness.locations.find(
      (loc) => loc.locationCode === animal.currentStatus.location
    ) ?? null;
  }, [allPetsFreshness.locations, animal?.currentStatus?.location]);

  const { data: profileData } = useQuery<AnimalProfileData>({
    queryKey: ["/api/animals", animalId, "profile"],
    enabled: !!animalId,
  });

  // S6: Evaluation checklist query
  interface EvaluationChecklistState {
    active: ChecklistItemDisplay[];
    pending: ChecklistItemDisplay[];
    dismissed: ChecklistItemDisplay[];
    trackId: string | null;
    trackName: string | null;
  }
  interface EvalTrackOption { id: string; name: string; isDefault: boolean; isActive: boolean; }

  const { data: evalState, isLoading: isEvalLoading } = useQuery<EvaluationChecklistState>({
    queryKey: ["/api/animals", animalId, "profile-checklist", "evaluation"],
    queryFn: async () => {
      const res = await fetch(`/api/animals/${animalId}/profile-checklist?type=evaluation`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch evaluation checklist");
      return res.json();
    },
    enabled: !!animalId,
  });

  const { data: evalTracks = [] } = useQuery<EvalTrackOption[]>({
    queryKey: ["/api/admin/evaluation-tracks"],
    enabled: !!animalId,
  });

  // Dismissed section expand state (not persisted)
  const [evalDismissedExpanded, setEvalDismissedExpanded] = useState(false);

  // Local state for the "Add track items" dropdown — starts empty so the card loads
  // without the pending queue visible. Selecting a track surfaces its pending items;
  // navigating away and back resets to the placeholder view.
  const [localTrackId, setLocalTrackId] = useState("");

  const evalChangeTrackMutation = useMutation({
    mutationFn: (trackId: string) =>
      apiRequest("PATCH", `/api/animals/${animalId}/evaluation-track`, { trackId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "profile-checklist", "evaluation"] });
    },
  });

  const evalAssignMutation = useMutation({
    mutationFn: ({ definitionId, status }: { definitionId: string; status: "assigned" | "dismissed" }) =>
      apiRequest("PATCH", `/api/animals/${animalId}/profile-checklist/${definitionId}/applicability`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "profile-checklist", "evaluation"] });
    },
  });

  const evalAssignAllMutation = useMutation({
    mutationFn: async (definitionIds: string[]) => {
      const results = await Promise.allSettled(
        definitionIds.map((id) =>
          apiRequest("PATCH", `/api/animals/${animalId}/profile-checklist/${id}/applicability`, { status: "assigned" }),
        ),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) throw new Error(`${failed} item(s) could not be assigned`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "profile-checklist", "evaluation"] });
    },
    onError: (err: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "profile-checklist", "evaluation"] });
      toast({ title: "Assign All", description: err.message, variant: "destructive" });
    },
  });

  // Gate S7: activate evaluation checklist for this animal (any authenticated user)
  const evalActivateMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/animals/${animalId}/evaluation-checklist/activate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "profile-checklist", "evaluation"] });
      navigate(`/animals/${animalId}?tab=behavior`);
    },
    onError: (err: Error) => {
      toast({ title: "Failed to create checklist", description: err.message, variant: "destructive" });
    },
  });

  const activeTracks = evalTracks.filter(t => t.isActive);
  // Non-default tracks only — "General" core items are always present and not selectable
  const selectableTracks = activeTracks.filter(t => !t.isDefault);
  const evalActiveComplete = evalState?.active.filter(i => i.isComplete).length ?? 0;
  const evalActiveTotal = evalState?.active.length ?? 0;

  useBreadcrumbs([
    { label: "Reporting" },
    { label: "Animals", href: "/animals" },
    { label: animal?.animalId || "Loading..." },
  ]);

  // R3 C1: Filter state for the behavior tab interaction list
  const [activeScoreFilter, setActiveScoreFilter] = useState<string | null>(null);
  const [activeUnscoredOnly, setActiveUnscoredOnly] = useState(false);
  const [activeFlagType, setActiveFlagType] = useState<string | null>(null);
  const [interactionNoteSearch, setInteractionNoteSearch] = useState("");
  const interactionScrollRef = useRef<HTMLDivElement>(null);
  const activityScrollRef = useRef<HTMLDivElement>(null);

  const { data: interactionsData, isLoading: interactionsLoading } = useQuery<InteractionsResponse>({
    queryKey: ['/api/interactions', animalId],
    queryFn: async () => {
      const response = await fetch(
        `/api/interactions?animalId=${animalId}&startDate=2000-01-01&endDate=2100-12-31&limit=10&sortBy=occurred_at&sortDir=desc`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to fetch interactions');
      return response.json();
    },
    enabled: !!animalId,
  });

  // Gate H2.2: Behavior context (rollups + trend) - independent fetch, graceful failure
  const { data: behaviorContext, isLoading: behaviorContextLoading, isError: behaviorContextError } = useQuery<BehaviorContext>({
    queryKey: ['/api/animals', animalId, 'behavior-context'],
    queryFn: async () => {
      const response = await fetch(
        `/api/animals/${animalId}/behavior-context`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to fetch behavior context');
      return response.json();
    },
    enabled: !!animalId,
    retry: 1, // Limit retries for graceful degradation
  });

  // Gate H2.6: Summary generation mutation
  const generateSummaryMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/animals/${animalId}/behavior-summary/generate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/animals', animalId, 'behavior-context'] });
      toast({
        title: "Summary generated",
        description: "The behavior summary has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Generation failed",
        description: error.message || "Failed to generate summary. Please try again.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/animals', animalId, 'behavior-context'] });
    },
  });


  // K10: Per-stay optimistic local state for outcome reason
  const [localOutcomeReasons, setLocalOutcomeReasons] = useState<Record<number, string>>({});

  const timelineStaysKey = animal?.timeline?.stays?.map(s => `${s.stayIndex}:${s.outcomeReason}`).join(',') ?? '';
  useEffect(() => {
    setLocalOutcomeReasons({});
  }, [timelineStaysKey]);

  const outcomeReasonMutation = useMutation({
    mutationFn: async ({ outcomeReason, stayIndex }: { outcomeReason: string; stayIndex?: number }) => {
      return api.patch(`/api/animals/${animalId}/outcome`, { outcomeReason, stayIndex });
    },
    onMutate: ({ outcomeReason, stayIndex }: { outcomeReason: string; stayIndex?: number }) => {
      const key = stayIndex ?? -1;
      setLocalOutcomeReasons(prev => ({ ...prev, [key]: outcomeReason }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/animals', animalId] });
      toast({
        title: "Outcome updated",
        description: "The outcome reason has been saved.",
      });
    },
    onError: (_err, { stayIndex }) => {
      const key = stayIndex ?? -1;
      setLocalOutcomeReasons(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "Failed to update outcome reason.",
      });
    },
  });

  // Gate I13: Open drawer instead of directly generating
  const handleGenerateSummary = () => {
    setSummaryDrawerOpen(true);
  };
  
  // Gate I13: Summary history query
  interface SummaryHistoryItem {
    id: number;
    timeframeType: string;
    customQuestions: string | null;
    preview: string | null;
    generatedAt: string;
  }
  
  interface SummaryHistoryResponse {
    animalId: string;
    summaries: SummaryHistoryItem[];
    totalCount: number;
  }
  
  const { data: summaryHistoryData, isLoading: historyLoading } = useQuery<SummaryHistoryResponse>({
    queryKey: ['/api/animals', animalId, 'summary-history'],
    queryFn: async () => {
      const response = await fetch(
        `/api/animals/${animalId}/summary-history`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to fetch summary history');
      return response.json();
    },
    enabled: !!animalId && canGenerateSummary,
  });
  
  const summaryHistory = summaryHistoryData?.summaries;

  // Gate E3.5.2: Scroll to top on mount to ensure header is visible (especially on mobile)
  // The layout uses <main className="overflow-auto"> as the scroll container, not window
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTop = 0;
    }
  }, [animalId]);

  // Gate I7: Track recently viewed animals for dashboard feature
  // Fire once per animal view, fail silently, invalidate cache on success
  useEffect(() => {
    if (!animalId) return;

    // Use api.post which includes CSRF token automatically
    api.post(`/api/animals/${animalId}/recent-view`)
      .then(() => {
        // Invalidate the recently viewed animals cache so dashboard shows update
        queryClient.invalidateQueries({ queryKey: ["/api/users/me/recent-animals"] });
      })
      .catch(() => {
        // Silently ignore errors - tracking shouldn't block the page
      });
  }, [animalId]);

  if (isLoading) {
    return (
      <div className="flex-1 space-y-6 px-6 pb-6 pt-4">
        {/* Gate E3.7.2: Global page header - loading state */}
        <PageHeader
          breadcrumb={[{ label: "Reporting" }, { label: "Animals", href: "/animals" }, { label: "Loading..." }]}
          title="Loading..."
        />
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-32 w-full" />
                </CardContent>
              </Card>
            </div>
            <div>
              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-48 w-full" />
                </CardContent>
              </Card>
            </div>
          </div>
      </div>
    );
  }

  if (!animal) {
    return (
      <div className="flex-1 space-y-6 px-6 pb-6 pt-4">
        {/* Gate E3.7.2: Global page header - not found state */}
        <PageHeader
          breadcrumb={[{ label: "Reporting" }, { label: "Animals", href: "/animals" }, { label: "Not Found" }]}
          title="Not Found"
        />
        <Card className="p-8">
            <div className="text-center text-muted-foreground">
              <Dog className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Animal not found</p>
            </div>
          </Card>
      </div>
    );
  }

  const data = animal;
  const SpeciesIcon = data.species === "CAT" ? Cat : Dog;


  // K10: Use authoritative outcomeDate from server
  const outcomedDate = data.outcomeDate ?? (data.isOutcomed ? data.currentStatus.lastSeenDate : null);

  // Gate E3.5.6: Share button handler - copy animal summary to clipboard
  const handleShare = async () => {
    if (typeof window === "undefined") return;
    const species = data.species === "DOG" ? "Dog" : data.species === "CAT" ? "Cat" : "Other";
    const url = window.location.href;
    const summary = `${data.name} (${species}) - ${data.animalId} - ${url}`;
    const ok = await copyToClipboard(summary);
    if (ok) {
      toast({ title: "Animal details copied", description: "Summary copied to clipboard." });
    } else {
      toast({ variant: "destructive", title: "Copy failed", description: "Unable to copy to clipboard. Please try again." });
    }
  };

  // Helper to get full status display
  const getStatusDisplay = () => {
    const status = data.currentStatus.kennelStatusNorm;
    const substatus = data.currentStatus.substatusNorm;
    if (substatus) {
      return `${status === "UNAVAIL" ? "UNAVAIL" : status} ${substatus}`;
    }
    return status || "Unknown";
  };


  return (
    <div className="flex-1 flex flex-col">
      {/* Gate H2.7: Fixed/Sticky Header with animal name, actions, and key fields */}
      <div 
        className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b px-6 py-3"
        data-testid="animal-fixed-header"
      >
        
        {/* Name row with avatar, name, ID, and actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <AnimalAvatar
              animalId={data.animalId}
              photoUrl={data.photoUrl}
              name={data.name}
              species={data.species}
              className="h-[42px] w-[42px] shrink-0"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold truncate" data-testid="header-animal-name">{data.name}</h1>
                <Badge variant="outline" className="font-mono shrink-0" data-testid="header-animal-id">
                  {data.animalId}
                </Badge>
                <Button
                  size="icon"
                  variant="ghost"
                  title="Copy Animal ID"
                  data-testid="button-copy-animal-id"
                  className="shrink-0"
                  onPointerDown={async (e) => {
                    e.preventDefault();
                    await copyToClipboard(data.animalId);
                    toast({ title: "Copied", description: `${data.animalId} copied to clipboard` });
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <StatusDaysBadge
                  status={data.currentStatus.kennelStatusNorm}
                  substatus={data.currentStatus.substatusNorm}
                  statusSince={data.currentStatus.kennelStatusSince}
                  isApprox={data.currentStatus.kennelStatusSinceIsApprox}
                  isOutcomed={data.isOutcomed}
                  data-testid="header-kennel-status"
                />
                <Badge variant="secondary" className="gap-1" data-testid="header-location">
                  <MapPin className="h-3 w-3" />
                  {data.currentStatus.location}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-testid="header-actions">
            <FollowAnimalButton
              animalId={data.animalId}
              animalName={data.name}
              variant="full"
              className="hidden md:inline-flex"
            />
            <Button variant="outline" onClick={() => setEntityLinkerOpen(true)} data-testid="button-add-to-task" className="hidden md:inline-flex">
              <LinkIcon className="h-4 w-4 mr-2" />
              Add to...
            </Button>
            <Button variant="outline" onClick={handleShare} data-testid="button-share-animal" className="hidden md:inline-flex">
              <Share className="h-4 w-4 mr-2" />
              Share
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="button-mobile-actions" className="md:hidden">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <FollowAnimalButton
                  animalId={data.animalId}
                  animalName={data.name}
                  variant="menu-item"
                />
                <DropdownMenuItem onClick={() => setEntityLinkerOpen(true)} data-testid="menu-add-to-task">
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Add to...
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleShare} data-testid="menu-share-animal">
                  <Share className="h-4 w-4 mr-2" />
                  Share
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-6 px-6 pb-6 pt-4">

        {/* K10: Outcomed banner with reason */}
        {data.isOutcomed && (() => {
          const lastTimelineStay = data.timeline?.stays?.[data.timeline.stays.length - 1];
          const currentStay = lastTimelineStay;
          const currentStayIndex = currentStay?.stayIndex;
          const totalStays = data.timeline?.stays?.length ?? 0;
          const bannerReason = currentStayIndex != null
            ? (localOutcomeReasons[currentStayIndex] ?? currentStay?.outcomeReason ?? data.outcomeReason ?? "unknown")
            : (localOutcomeReasons[-1] ?? data.outcomeReason ?? "unknown");
          const stayColor = currentStayIndex != null ? getStayColor(currentStayIndex) : undefined;
          const multipleStays = totalStays > 1;
          const stayStartDate = currentStay?.startDate;
          const effectiveOutcomeDate = outcomedDate ?? currentStay?.endDate;
          let durationDays: number | null = null;
          if (stayStartDate && effectiveOutcomeDate) {
            const start = new Date(stayStartDate + "T00:00:00Z");
            const end = new Date(effectiveOutcomeDate + (effectiveOutcomeDate.includes("T") ? "" : "T00:00:00Z"));
            durationDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
          }
          return (
            <Card
              data-testid="animal-outcomed-banner"
              style={{
                borderColor: stayColor ?? undefined,
                backgroundColor: stayColor ? `color-mix(in srgb, ${stayColor} 6%, transparent)` : undefined,
              }}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge variant="secondary" data-testid="badge-outcomed">
                    Outcomed{currentStayIndex != null ? `: Stay ${currentStayIndex}` : ""}{multipleStays ? ` of ${totalStays}` : ""}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {data.name} was{multipleStays ? " most recently" : ""} outcomed
                    {effectiveOutcomeDate && (
                      <> on <span className="font-medium text-foreground">{formatDateUTC(effectiveOutcomeDate, "MMM d, yyyy")}</span></>
                    )}
                    {durationDays != null && (
                      <> and was in the shelter for <span className="font-medium text-foreground">{durationDays} {durationDays === 1 ? "day" : "days"}</span></>
                    )}.
                  </span>
                  <span className="text-sm text-muted-foreground">Outcome reason:</span>
                  <Select
                    value={bannerReason}
                    onValueChange={(value) => outcomeReasonMutation.mutate({ outcomeReason: value, stayIndex: currentStayIndex })}
                    disabled={outcomeReasonMutation.isPending}
                  >
                    <SelectTrigger className="w-[160px]" data-testid="select-outcome-reason">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adopted">Adopted</SelectItem>
                      <SelectItem value="transferred">Transferred</SelectItem>
                      <SelectItem value="returned_to_owner">Returned to Owner</SelectItem>
                      <SelectItem value="foster">Foster</SelectItem>
                      <SelectItem value="euthanized">Euthanized</SelectItem>
                      <SelectItem value="died_in_care">Died in Care</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="flex items-center justify-between gap-2">
            <TabsList className="h-11 p-1" data-testid="tabs-animal-detail">
              <TabsTrigger
                value="overview"
                className="h-9 px-4 text-sm font-medium gap-2"
                data-testid="tab-overview"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger
                value="profile"
                className="h-9 px-4 text-sm font-medium gap-2"
                data-testid="tab-profile"
              >
                <SpeciesIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Profile</span>
              </TabsTrigger>
              <TabsTrigger
                value="behavior"
                className="h-9 px-4 text-sm font-medium gap-2"
                data-testid="tab-behavior"
              >
                <Award className="h-4 w-4" />
                <span className="hidden sm:inline">Behavior</span>
              </TabsTrigger>
              <TabsTrigger
                value="stays"
                className="h-9 px-4 text-sm font-medium gap-2"
                data-testid="tab-stays"
              >
                <Clock className="h-4 w-4" />
                <span className="hidden sm:inline">Stays</span>
              </TabsTrigger>
              <TabsTrigger
                value="medical"
                className="h-9 px-4 text-sm font-medium gap-2"
                data-testid="tab-medical"
              >
                <Stethoscope className="h-4 w-4" />
                <span className="hidden sm:inline">Medical</span>
              </TabsTrigger>
              <TabsTrigger
                value="files"
                className="h-9 px-4 text-sm font-medium gap-2"
                data-testid="tab-files"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Files</span>
              </TabsTrigger>
            </TabsList>
            {locationFreshness?.lastReportLocalDate && (() => {
              const isFresh = locationFreshness.isCurrent;
              const isCritical = !isFresh && locationFreshness.staleDays >= 2;
              const colorClass = isFresh
                ? "text-emerald-600 dark:text-emerald-500"
                : isCritical
                  ? "text-red-600 dark:text-red-400"
                  : "text-amber-600 dark:text-amber-500";
              const displayShort = format(
                new Date(locationFreshness.lastReportLocalDate + "T00:00:00"),
                "EEE, MMM d, yyyy"
              );
              return (
                <>
                  <button
                    type="button"
                    className={`flex items-center gap-1.5 shrink-0 ${colorClass} hover-elevate rounded-sm px-1`}
                    onClick={() => setFreshnessModalOpen(true)}
                    data-testid="data-last-updated"
                  >
                    <span className="hidden sm:inline text-xs whitespace-nowrap">
                      Data Last Updated: {displayShort}
                    </span>
                    <Info className="h-4 w-4 shrink-0" />
                  </button>
                  <ReportFreshnessModal
                    open={freshnessModalOpen}
                    onOpenChange={setFreshnessModalOpen}
                    staleLocations={[
                      ...allPetsFreshness.staleLocations.map(l => ({ ...l, sourceReportType: "allPets" as const })),
                      ...interactionFreshness.staleLocations.map(l => ({ ...l, sourceReportType: "interaction" as const })),
                    ]}
                    locationsWithGaps={[
                      ...allPetsFreshness.locationsWithGaps.map(l => ({ ...l, sourceReportType: "allPets" as const })),
                      ...interactionFreshness.locationsWithGaps.map(l => ({ ...l, sourceReportType: "interaction" as const })),
                    ]}
                    reportTypes={["allPets", "interaction"]}
                    allPetsLocations={allPetsFreshness.locations}
                    interactionLocations={interactionFreshness.locations}
                  />
                </>
              );
            })()}
          </div>

          <TabsContent value="overview" className="mt-4" data-testid="content-overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:items-start">
              <Card>
                <CardContent className="pt-6">
                  {/* Section 1: Animal Details */}
                  <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3">Animal Details</p>
                  {(() => {
                    const stays = data.timeline?.stays;
                    const admissionStays = (stays ?? []).filter(s => !s.linkedToPriorStay);
                    const admissionCount = admissionStays.length || (stays?.length ? 1 : 0);
                    const firstStay = admissionStays[0] ?? stays?.[0];
                    const mostRecentStay = admissionCount > 0 ? admissionStays[admissionCount - 1] : undefined;
                    const firstIntakeDateStr = firstStay?.snapshots?.[0]?.intakeDate ?? firstStay?.startDate;
                    const mostRecentIntakeDateStr =
                      mostRecentStay?.snapshots?.[0]?.intakeDate ??
                      mostRecentStay?.startDate ??
                      data.demographics?.intakeDate;
                    const hasMultipleStays = firstIntakeDateStr && mostRecentIntakeDateStr && firstIntakeDateStr !== mostRecentIntakeDateStr;
                    const totalDbStays = stays?.length ?? 0;
                    const stayLabel = (admissionCount === 1 && totalDbStays > 1)
                      ? `(1 Admission · ${totalDbStays} Locations)`
                      : `(${admissionCount} Stay${admissionCount !== 1 ? "s" : ""})`;
                    const daysHere = mostRecentIntakeDateStr
                      ? differenceInDays(new Date(), new Date(mostRecentIntakeDateStr))
                      : null;
                    return (
                      <div className="space-y-5">
                        {/* At-a-glance chips */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="rounded-md bg-muted/50 px-3 py-2.5">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <MapPin className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Kennel</span>
                            </div>
                            <span className="text-sm font-semibold">{data.currentStatus.zoneCode ? resolveLabel(data.currentStatus.zoneCode) : "–"}</span>
                          </div>
                          <div className="rounded-md bg-muted/50 px-3 py-2.5">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Current LOS</span>
                            </div>
                            <span className="text-sm font-semibold">{daysHere !== null ? `${daysHere}d` : "–"}</span>
                          </div>
                          <div className="rounded-md bg-muted/50 px-3 py-2.5">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <Award className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Adoption Level</span>
                            </div>
                            <span className="text-sm font-semibold">{data.currentStatus.adoptionLevelNorm || "–"}</span>
                          </div>
                          <div className="rounded-md bg-muted/50 px-3 py-2.5">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <Calendar className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Age</span>
                            </div>
                            <span className="text-sm font-semibold">{data.demographics?.ageDisplay || "–"}</span>
                          </div>
                        </div>

                        {/* Intake subsection */}
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Intake</p>
                          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                            {hasMultipleStays ? (
                              <>
                                <div className="flex items-center gap-1.5">
                                  <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                  <span className="text-sm text-muted-foreground" data-testid="text-first-intake-date">First Intake</span>
                                </div>
                                <span className="text-sm font-medium">{format(new Date(firstIntakeDateStr), "EEE, MMM d, yyyy")}</span>
                                <div className="flex items-center gap-1.5">
                                  <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                  <span className="text-sm text-muted-foreground" data-testid="text-intake-date">Latest Intake</span>
                                </div>
                                <span className="text-sm font-medium">
                                  {format(new Date(mostRecentIntakeDateStr), "EEE, MMM d, yyyy")}{" "}
                                  <button type="button" className="text-primary hover:underline cursor-pointer" onClick={() => handleTabChange("stays")} data-testid="link-stay-count">
                                    {stayLabel}
                                  </button>
                                </span>
                              </>
                            ) : mostRecentIntakeDateStr ? (
                              <>
                                <div className="flex items-center gap-1.5">
                                  <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                  <span className="text-sm text-muted-foreground" data-testid="text-intake-date">First Intake</span>
                                </div>
                                <span className="text-sm font-medium">
                                  {format(new Date(mostRecentIntakeDateStr), "EEE, MMM d, yyyy")}{" "}
                                  <button type="button" className="text-primary hover:underline cursor-pointer" onClick={() => handleTabChange("stays")} data-testid="link-stay-count">
                                    {stayLabel}
                                  </button>
                                </span>
                              </>
                            ) : null}
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="text-sm text-muted-foreground">Last Report</span>
                            </div>
                            <span className="text-sm font-medium">{formatRelativeDate(data.currentStatus.lastSeenDate)}</span>
                          </div>
                        </div>

                        {/* Characteristics subsection */}
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Characteristics</p>
                          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                            <PawPrint className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" />
                            <span className="text-sm font-medium">{data.species}</span>

                            {data.primaryBreed && (
                              <>
                                <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" />
                                <span className="text-sm font-medium">{data.primaryBreed}</span>
                              </>
                            )}

                            {data.color && (
                              <>
                                <Palette className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" />
                                <span className="text-sm font-medium">{data.color}</span>
                              </>
                            )}

                            {data.sex && (
                              <>
                                <User className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" />
                                <span className="text-sm font-medium">{data.sex}</span>
                              </>
                            )}

                            {data.demographics?.weightLb && (
                              <>
                                <Weight className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" />
                                <span className="text-sm font-medium">{data.demographics.weightLb} lbs</span>
                              </>
                            )}

                            {(data.demographics?.originHistory?.length || data.demographics?.originOrgNorm) && (
                              <>
                                <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" />
                                <span className="text-sm font-medium">
                                  {data.demographics.originHistory?.length
                                    ? <>
                                        {data.demographics.originHistory[0]}
                                        {data.demographics.originHistory.length > 1 && (
                                          <span className="text-muted-foreground font-normal">
                                            {", " + data.demographics.originHistory.slice(1).join(", ")}
                                          </span>
                                        )}
                                      </>
                                    : data.demographics.originOrgNorm}
                                </span>
                              </>
                            )}

                            {data.microchipId && (
                              <>
                                <Cpu className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" />
                                <span className="text-sm font-mono">{data.microchipId}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Section 2: Adoption Profile */}
                  {profileData && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Adoption Profile</p>
                        <Link href={`/animals/${animalId}?tab=profile`} className="flex items-center gap-1 text-xs text-primary hover:underline shrink-0">
                          View profile <ArrowRight className="h-3 w-3" />
                        </Link>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Adoption Profile Status</span>
                        <PublicAdoptionListingStatusBadge status={profileData.profileStatus} />
                      </div>
                      <AnimalChecklistProgress
                        checklistType="readiness"
                        done={profileData.checklistState.filter(i => i.isComplete).length}
                        total={profileData.checklistState.length}
                      />
                    </div>
                  )}
                  {/* Section 3: Behavior */}
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Behavior</p>
                      <Link href={`/animals/${animalId}?tab=behavior`} className="flex items-center gap-1 text-xs text-primary hover:underline shrink-0">
                        View behavior <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                      <span className="text-sm text-muted-foreground py-0.5">Eval Status</span>
                      {data.evaluation.status !== null ? (
                        <span className="text-sm font-medium py-0.5"><LabelWithHover label={data.evaluation.label ?? ""} hoverContent={null} /></span>
                      ) : (
                        <span className="text-sm text-muted-foreground py-0.5">Not evaluated</span>
                      )}
                    </div>
                    {evalActiveTotal > 0 ? (
                      <AnimalChecklistProgress
                        checklistType="evaluation"
                        done={evalActiveComplete}
                        total={evalActiveTotal}
                      />
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => evalActivateMutation.mutate()}
                        disabled={evalActivateMutation.isPending}
                        data-testid="button-overview-create-eval-checklist"
                      >
                        <ClipboardList className="h-3.5 w-3.5" />
                        {evalActivateMutation.isPending ? "Creating…" : "Create Eval Checklist"}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="flex flex-col overflow-hidden lg:h-[560px]">
                <Tabs defaultValue="activity" className="flex flex-col min-h-0 flex-1">
                  <div className="px-6 pt-4 shrink-0">
                    <TabsList data-testid="tabs-activity-reports">
                      <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
                      <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
                    </TabsList>
                  </div>
                  <CardContent ref={activityScrollRef} className="flex-1 min-h-0 overflow-y-auto">
                    <TabsContent value="activity" className="mt-0">
                      <AnimalActivityTimeline
                        animalId={animalId!}
                        animalName={data.name}
                        onObservationClick={(id) => {
                          apiRequest("GET", `/api/interactions/${id}`).then((full) => {
                            setSelectedInteraction(full as Interaction);
                            setInteractionDrawerOpen(true);
                          });
                        }}
                        category={activityCategory}
                        onCategoryChange={setActivityCategory}
                        dataStartDate={data.dataStartDate}
                        intakeDate={data.intakeDate}
                        scrollContainerRef={activityScrollRef}
                      />
                    </TabsContent>
                    <TabsContent value="reports" className="mt-0">
                      <LinkedReportsList animalId={animalId!} />
                    </TabsContent>
                  </CardContent>
                </Tabs>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-4" data-testid="content-profile">
            <AnimalProfileTab animalId={animalId!} animalName={data.name} animalSpecies={data.species} />
          </TabsContent>

          <TabsContent value="behavior" className="mt-4" data-testid="content-behavior">
            {/* Behavior tab: single grid — desktop 2-row layout, mobile stack order differs */}
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 items-stretch">
              <Card className="order-1 lg:col-span-6 lg:row-start-1">
                <CardHeader>
                  <CardTitle>Behavior</CardTitle>
                  <CardDescription>Evaluation status and score breakdown</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                    <span className="text-sm text-muted-foreground py-0.5">Eval Status</span>
                    {data.evaluation.status !== null ? (
                      <span className="text-sm font-medium py-0.5" data-testid="badge-evaluation-status">
                        <LabelWithHover label={data.evaluation.label ?? ""} hoverContent={null} />
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground py-0.5" data-testid="badge-evaluation-none">
                        Not evaluated
                      </span>
                    )}

                  </div>

                  <div className="space-y-1.5" data-testid="eval-status-history-section">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium">Eval Status History</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60 cursor-pointer shrink-0" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px] text-xs">
                          Day counts are totaled across all of this animal's admissions. If an animal was previously adopted and later returned, its evaluation history from each stay is combined into these totals.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <EvalStatusStrip animalId={data.animalId} />
                  </div>

                  <BehaviorRollupsSection 
                    rollups={behaviorContext?.rollups}
                    alerts={behaviorContext?.alerts}
                    animalId={data.animalId}
                    isLoading={behaviorContextLoading}
                    isError={behaviorContextError}
                    activeScoreFilter={activeScoreFilter}
                    activeUnscoredOnly={activeUnscoredOnly}
                    onScoreSelect={(code) => {
                      setActiveScoreFilter(code);
                      setActiveUnscoredOnly(false);
                    }}
                    onUnscoredSelect={() => {
                      setActiveUnscoredOnly(prev => !prev);
                      setActiveScoreFilter(null);
                    }}
                  />

                </CardContent>
              </Card>

              {/* S6: Evaluation Checklist card — desktop: row 1 right (40%); mobile: below interactions */}
              <Card id="evaluation-checklist" data-testid="card-evaluation-checklist" className="order-4 lg:col-span-4 lg:row-start-1 flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3 flex-wrap min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <CardTitle>Evaluation Checklist</CardTitle>
                  </div>
                  {/* Track selector — right-aligned; hidden when no non-default tracks exist or checklist not yet activated */}
                  {selectableTracks.length > 0 && evalState?.trackId !== "" && (
                    <div className="flex items-center gap-1">
                      <Select
                        value={localTrackId}
                        onValueChange={(v) => { setLocalTrackId(v); evalChangeTrackMutation.mutate(v); }}
                        disabled={evalChangeTrackMutation.isPending}
                      >
                        <SelectTrigger className="h-8 text-sm w-[180px] border-dashed" data-testid="select-eval-track">
                          <SelectValue placeholder="Add Eval Items…" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectableTracks.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {localTrackId !== "" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 shrink-0"
                          onClick={() => setLocalTrackId("")}
                          disabled={evalChangeTrackMutation.isPending}
                          aria-label="Clear track selection"
                          data-testid="button-clear-eval-track"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  )}

                </div>
                {evalActiveTotal > 0 && (
                  <AnimalChecklistProgress
                    checklistType="evaluation"
                    done={evalActiveComplete}
                    total={evalActiveTotal}
                  />
                )}
              </CardHeader>

              <CardContent className="space-y-4 pt-0 flex-1 flex flex-col">
                  {/* Pending review queue — only shown when admin has explicitly selected a track */}
                  {localTrackId !== "" && (evalState?.pending.length ?? 0) > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Eval Items: {evalState!.trackName}
                          </p>
                          <Badge variant="secondary" className="text-xs" data-testid="badge-eval-pending-count">
                            {evalState!.pending.length} available
                          </Badge>
                        </div>
                        {user?.isAdmin && (
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
                            disabled={evalAssignAllMutation.isPending}
                            onClick={() =>
                              evalAssignAllMutation.mutate(
                                evalState!.pending.map((i) => i.definitionId),
                              )
                            }
                            data-testid="button-eval-assign-all"
                          >
                            {evalAssignAllMutation.isPending ? "Assigning…" : "Assign All"}
                          </button>
                        )}
                      </div>
                      <div className="divide-y">
                        {evalState!.pending.map((item) => (
                          <EvaluationChecklistPendingItem
                            key={item.definitionId}
                            item={item}
                            animalId={animalId!}
                            onResolved={() =>
                              queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "profile-checklist", "evaluation"] })
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Separator between pending queue and active checklist */}
                  {localTrackId !== "" && (evalState?.pending.length ?? 0) > 0 && (evalState?.active.length ?? 0) > 0 && (
                    <Separator />
                  )}

                  {/* Active checklist — core and conditional sections */}
                  {(evalState?.active.length ?? 0) > 0 && (() => {
                    const active = evalState!.active;
                    const coreItems = active.filter(i => !i.evaluationTrackId);
                    const conditionalItems = active.filter(i => !!i.evaluationTrackId);
                    // Group conditional items by their own track name (not the animal's current track)
                    const conditionalByTrack = new Map<string, typeof active>();
                    for (const item of conditionalItems) {
                      const key = item.evaluationTrackName ?? "Other";
                      if (!conditionalByTrack.has(key)) conditionalByTrack.set(key, []);
                      conditionalByTrack.get(key)!.push(item);
                    }
                    const renderItem = (item: typeof active[0], suppressTrackBadge = false) => (
                      <AdoptionListingChecklistItem
                        key={item.definitionId}
                        item={suppressTrackBadge ? { ...item, evaluationTrackName: null } : item}
                        onToggle={(definitionId, isComplete) => {
                          apiRequest("PATCH", `/api/animals/${animalId}/profile-checklist/${definitionId}`, { isComplete })
                            .then(() => queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "profile-checklist", "evaluation"] }));
                        }}
                        isLoading={evalAssignMutation.isPending}
                        onDismiss={user?.isAdmin ? (definitionId) => {
                          evalAssignMutation.mutate({ definitionId, status: "dismissed" });
                        } : undefined}
                      />
                    );
                    return (
                      <div className="space-y-3">
                        {coreItems.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              General
                            </p>
                            <div className="space-y-1 list-zebra">
                              {coreItems.map(i => renderItem(i))}
                            </div>
                          </div>
                        )}
                        {Array.from(conditionalByTrack.entries()).map(([trackLabel, items]) => (
                          <div key={trackLabel} className="space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              {trackLabel}
                            </p>
                            <div className="space-y-1 list-zebra">
                              {items.map(i => renderItem(i, true))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Gate S7: not-activated state — trackId === "" means evaluation_track_id IS NULL */}
                  {!isEvalLoading && evalState?.trackId === "" && (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center" data-testid="div-eval-checklist-not-activated">
                      <ClipboardList className="h-8 w-8 text-muted-foreground/30" />
                      <div className="flex flex-col items-center gap-2">
                        <Button
                          variant="default"
                          onClick={() => evalActivateMutation.mutate()}
                          disabled={evalActivateMutation.isPending}
                          data-testid="button-create-eval-checklist"
                        >
                          {evalActivateMutation.isPending ? "Creating…" : "Create Checklist"}
                        </Button>
                        <p className="text-sm text-muted-foreground">
                          Track evaluation progress for {data?.name}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Not Applicable section */}
                  {(evalState?.dismissed.length ?? 0) > 0 && (
                    <div className="space-y-1">
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs font-medium text-muted-foreground"
                        onClick={() => setEvalDismissedExpanded(p => !p)}
                        data-testid="button-eval-dismissed-toggle"
                      >
                        {evalDismissedExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        Not Applicable ({evalState!.dismissed.length})
                      </button>
                      {evalDismissedExpanded && (
                        <div className="divide-y opacity-70">
                          {evalState!.dismissed.map((item) => (
                            <div key={item.definitionId} className="flex items-center justify-between gap-3 py-2 px-2" data-testid={`div-dismissed-item-${item.definitionId}`}>
                              <span className="text-sm line-through truncate text-muted-foreground">{item.name}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => evalAssignMutation.mutate({ definitionId: item.definitionId, status: "assigned" })}
                                disabled={evalAssignMutation.isPending}
                                data-testid={`button-reassign-dismissed-${item.definitionId}`}
                              >
                                Assign
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
            </Card>
              <Card className="order-2 lg:col-span-5 lg:row-start-2 flex flex-col overflow-hidden max-h-[560px]">
                <CardHeader>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs font-medium uppercase tracking-wider flex items-center gap-1">
                        <Activity className="w-3 h-3" /> Interactions
                      </Label>
                      {!interactionsLoading && (
                        <Badge
                          variant="secondary"
                          className="text-xs"
                          data-testid="badge-interaction-count"
                        >
                          {interactionsData?.pagination.total || 0}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={activeFlagType ?? "all"}
                        onValueChange={(v) => setActiveFlagType(v === "all" ? null : v)}
                      >
                        <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="select-interaction-flag-type">
                          <SelectValue placeholder="Any flag type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any flag type</SelectItem>
                          <SelectItem value="long_note">Long note</SelectItem>
                          <SelectItem value="keyword">Keyword match</SelectItem>
                          <SelectItem value="highlighted">Highlighted</SelectItem>
                        </SelectContent>
                      </Select>
                      <Link href={`/interactions?animalId=${data.animalId}&startDate=2000-01-01&endDate=2100-12-31`} asChild>
                        <Button variant="ghost" size="sm" className="gap-1" data-testid="link-view-all-interactions">
                          View all
                          <ArrowRight className="h-3 w-3" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <AnimalHighlightedInteractionCount animalId={data.animalId} />
                  <div className="relative pt-2">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      value={interactionNoteSearch}
                      onChange={(e) => setInteractionNoteSearch(e.target.value)}
                      placeholder="Search notes (e.g. leash redirect)…"
                      className="h-8 pl-7 pr-7 text-xs"
                      data-testid="input-note-search"
                    />
                    {interactionNoteSearch && (
                      <button
                        onClick={() => setInteractionNoteSearch("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-sm opacity-70 hover:opacity-100"
                        data-testid="button-clear-note-search"
                        aria-label="Clear search"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent ref={interactionScrollRef} className="px-0 pb-0 flex-1 min-h-0 overflow-y-auto">
                  <AnimalInteractionTimeline
                    animalId={animalId}
                    activeScoreFilter={activeScoreFilter}
                    onScoreClear={() => setActiveScoreFilter(null)}
                    activeUnscoredOnly={activeUnscoredOnly}
                    onUnscoredClear={() => setActiveUnscoredOnly(false)}
                    activeFlagType={activeFlagType}
                    onFlagTypeChange={setActiveFlagType}
                    dataStartDate={data.dataStartDate}
                    intakeDate={data.intakeDate}
                    onInteractionClick={(interaction) => {
                      setSelectedInteraction(interaction);
                      setInteractionDrawerOpen(true);
                    }}
                    scrollContainerRef={interactionScrollRef}
                    noteSearch={interactionNoteSearch}
                  />
                </CardContent>
              </Card>

              {isAiLoading ? (
                <Card className="order-3 lg:col-span-5 lg:row-start-2">
                  <CardContent className="pt-6 space-y-3" data-testid="skeleton-ai-summary">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ) : isAiEnabled ? (
                <Card className="order-3 lg:col-span-5 lg:row-start-2">
                  <CardContent className="pt-6 space-y-4">
                    <AISummarySection
                      summary={behaviorContext?.summary}
                      animalId={data.animalId}
                      isLoading={behaviorContextLoading}
                      onGenerate={handleGenerateSummary}
                      isGenerating={generateSummaryMutation.isPending}
                      canGenerate={canGenerateSummary}
                    />

                    {canGenerateSummary && (
                      <SummaryHistorySection
                        history={summaryHistory}
                        isLoading={historyLoading}
                        animalId={data.animalId}
                      />
                    )}
                  </CardContent>
                </Card>
              ) : null}
            </div>{/* end behavior tab grid */}
          </TabsContent>

          <TabsContent value="stays" className="mt-4" data-testid="content-stays">
            <AnimalTimelineCard
              timeline={data.timeline ?? null}
              animalId={data.animalId}
              onOutcomeReasonChange={(reason, stayIndex) => outcomeReasonMutation.mutate({ outcomeReason: reason, stayIndex })}
              isOutcomeReasonPending={outcomeReasonMutation.isPending}
              localOutcomeReasons={localOutcomeReasons}
            />
          </TabsContent>

          <TabsContent value="files" className="mt-4" data-testid="content-files">
            <AnimalFilesTab animalId={data.animalId} highlightFileId={searchParams.get("fileId")} />
          </TabsContent>

          <TabsContent value="medical" className="mt-4" data-testid="content-medical">
            <Card>
              <CardHeader>
                <CardTitle>Medical</CardTitle>
                <CardDescription>Medical notes from most recent report</CardDescription>
              </CardHeader>
              <CardContent>
                {Array.isArray(data.medical?.medicalDx) && data.medical.medicalDx.length > 0 ? (
                  <div className="space-y-6">
                    {data.medical.medicalDx.some(note => note.startsWith("DX-")) && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 mb-3">
                          <Stethoscope className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-semibold">Diagnoses</span>
                        </div>
                        {data.medical.medicalDx
                          .filter(note => note.startsWith("DX-"))
                          .map((dx, i) => {
                            const stripped = dx.replace("DX-", "").trim();
                            const [text, date] = stripped.split("\t");
                            return (
                              <div key={i} className="flex items-baseline gap-3 py-2 border-b border-border/50 last:border-0 pl-6">
                                <span className="text-sm">{text}</span>
                                {date && <span className="text-xs text-muted-foreground shrink-0">{date}</span>}
                              </div>
                            );
                          })}
                      </div>
                    )}

                    {data.medical.medicalDx.some(note => note.startsWith("CI-")) && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 mb-3">
                          <Heart className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-semibold">Care Instructions</span>
                        </div>
                        {data.medical.medicalDx
                          .filter(note => note.startsWith("CI-"))
                          .map((ci, i) => {
                            const stripped = ci.replace("CI-", "").trim();
                            const [text, date] = stripped.split("\t");
                            return (
                              <div key={i} className="flex items-baseline gap-3 py-2 border-b border-border/50 last:border-0 pl-6">
                                <span className="text-sm">{text}</span>
                                {date && <span className="text-xs text-muted-foreground shrink-0">{date}</span>}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    <Stethoscope className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No medical notes recorded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Gate I13: Summary Generation Drawer */}
      <AnimalSummaryDrawer
        open={summaryDrawerOpen}
        onOpenChange={setSummaryDrawerOpen}
        animalId={data.animalId}
        animalName={data.name}
      />
      
      {/* J6: Unified Entity Linker Dialog */}
      <EntityLinkerDialog
        open={entityLinkerOpen}
        onOpenChange={setEntityLinkerOpen}
        entityType="animal"
        entityId={animalId!}
        entityLabel={data?.name}
        animalSpecies={data?.species}
        animalPhotoUrl={data?.photoUrl}
      />

      {/* Interaction Detail Drawer */}
      <InteractionDetailDrawer
        open={interactionDrawerOpen}
        onOpenChange={(open) => {
          setInteractionDrawerOpen(open);
          if (!open) {
            setSelectedInteraction(null);
          }
        }}
        interaction={selectedInteraction ? {
          id: selectedInteraction.id,
          animalId: selectedInteraction.animalId,
          animalName: selectedInteraction.animalName,
          kennelStatusNorm: selectedInteraction.kennelStatusNorm ?? null,
          evaluationStatus: selectedInteraction.evaluationStatus ?? null,
          locationCode: selectedInteraction.locationCode,
          zoneCode: selectedInteraction.zoneCode ?? "",
          eventType: selectedInteraction.eventType,
          scoreCode: selectedInteraction.scoreCode,
          scoreBand: selectedInteraction.scoreBand,
          occurredAt: selectedInteraction.occurredAt,
          author: selectedInteraction.author,
          rawNote: selectedInteraction.rawNote,
          sourceReportId: selectedInteraction.sourceReportId ?? null,
          sourceEmailId: selectedInteraction.sourceEmailId ?? null,
          isFlagged: selectedInteraction.isFlagged ?? false,
          flagReasons: selectedInteraction.flagReasons ?? null,
        } : null}
        animalId={data.animalId}
        onInteractionChange={(newInteraction) => {
          setSelectedInteraction(newInteraction);
        }}
      />
    </div>
  );
}